import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SubscriptionSuccess = ({ onRefreshUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [isRestoring, setIsRestoring] = useState(false);
  const maxRetries = 20; // Increased retries
  const retryDelay = 2500; // 2.5 seconds between retries

  const getAuthHeader = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Restore access directly from Stripe
  const handleRestoreAccess = async () => {
    setIsRestoring(true);
    try {
      const response = await axios.post(
        `${API}/subscription/restore`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.status === 'success') {
        setStatus('success');
        if (onRefreshUser) {
          await onRefreshUser();
        }
      } else {
        setError(response.data.message || 'Could not restore access. Please contact support.');
      }
    } catch (err) {
      console.error('Error restoring access:', err);
      setError(err.response?.data?.detail || 'Failed to restore access. Please contact support.');
    } finally {
      setIsRestoring(false);
    }
  };

  const verifyPayment = useCallback(async () => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found');
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setStatus('error');
      setError('Not authenticated');
      return;
    }

    try {
      console.log(`Verifying payment (attempt ${retryCount + 1}/${maxRetries})...`);
      
      const response = await axios.get(`${API}/subscription/verify/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Verification response:', response.data);

      if (response.data.status === 'success') {
        setStatus('success');
        // Refresh user data to get updated subscription status
        if (onRefreshUser) {
          await onRefreshUser();
        }
      } else if (response.data.status === 'pending') {
        // Payment still processing, retry after delay
        if (retryCount < maxRetries) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, retryDelay);
        } else {
          // Max retries - try restore access as fallback
          setStatus('restoring');
        }
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      
      // If it's a Stripe error about session, the payment might have succeeded
      // Try to refresh user data anyway
      if (onRefreshUser) {
        try {
          const userData = await onRefreshUser();
          if (userData?.is_subscribed) {
            setStatus('success');
            return;
          }
        } catch (e) {
          console.error('Error refreshing user:', e);
        }
      }
      
      if (retryCount < maxRetries) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, retryDelay);
      } else {
        // Max retries - show restore option
        setStatus('restoring');
      }
    }
  }, [searchParams, onRefreshUser, retryCount, maxRetries]);

  // Auto-trigger restore when max retries reached
  useEffect(() => {
    if (status === 'restoring') {
      handleRestoreAccess();
    }
  }, [status]);

  useEffect(() => {
    if (status === 'checking') {
      verifyPayment();
    }
  }, [retryCount]); // Only re-run when retryCount changes

  const handleContinue = () => {
    navigate('/');
  };

  const handleRetry = () => {
    setStatus('checking');
    setRetryCount(0);
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4" data-testid="subscription-success-page">
      <div className="max-w-md w-full text-center space-y-6">
        {(status === 'checking' || status === 'restoring') && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-red-500 animate-spin" />
            <h2 className="font-heading text-2xl font-bold text-zinc-100">
              {status === 'restoring' ? "Confirming Your Subscription..." : "Verifying Payment..."}
            </h2>
            <p className="text-zinc-400">
              {status === 'restoring' 
                ? "We're syncing your payment with our system. This may take a moment."
                : "Please wait while we confirm your subscription"
              }
            </p>
            {status === 'checking' && (
              <p className="text-sm text-zinc-500">Attempt {retryCount + 1} of {maxRetries}</p>
            )}
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-zinc-100">
              Welcome to InSpiteOf!
            </h2>
            <p className="text-zinc-400">
              Your subscription is now active. You have full access to the Private Signal Network.
            </p>
            <Button
              onClick={handleContinue}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg"
              data-testid="continue-btn"
            >
              Start Exploring
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-zinc-100">
              Verification Issue
            </h2>
            <p className="text-zinc-400">{error}</p>
            <div className="space-y-3">
              <Button
                onClick={handleRestoreAccess}
                disabled={isRestoring}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg"
                data-testid="restore-access-btn"
              >
                {isRestoring ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Restoring Access...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Restore My Access
                  </>
                )}
              </Button>
              <Button
                onClick={handleRetry}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg"
                data-testid="retry-btn"
              >
                Try Verification Again
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const SubscriptionCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4" data-testid="subscription-cancel-page">
      <div className="max-w-md w-full text-center space-y-6">
        <h2 className="font-heading text-2xl font-bold text-zinc-100">
          Subscription Cancelled
        </h2>
        <p className="text-zinc-400">
          No worries! You can subscribe anytime to get full access to the Private Signal Network.
        </p>
        <Button
          onClick={() => navigate('/')}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg"
          data-testid="back-home-btn"
        >
          Back to Home
        </Button>
      </div>
    </div>
  );
};
